// DOM utilities for capturing and painting text highlights inside a live container element.
//
// Offsets are character positions within the container's combined text nodes, so they're
// independent of whatever markup (bold, lists, images...) the text contains. Painting only ever
// splits text nodes and wraps the pieces in <mark>s - element nodes are never cloned or moved -
// so a highlight crossing formatting (e.g. half of a bold word) is just several adjacent marks.
//
// Originally by Mark Burton (HD 9205, text 5 cs:3263).

const MARK_ATTR = 'data-xams-hl';
const HIGHLIGHT_COLOUR = '#FFD700';

// all text nodes inside container, in document order
const getTextNodes = (container) => {
	const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
	const nodes = [];
	let node;
	while ((node = walker.nextNode())) {
		nodes.push(node);
	}
	return nodes;
};

// converts the current browser selection into {start, end} offsets within container, or null
// when there's no non-empty selection inside it
const getSelectionOffsets = (container) => {
	const selection = window.getSelection();
	if (!selection || selection.rangeCount === 0) {
		return null;
	}

	const range = selection.getRangeAt(0);
	if (range.collapsed || !container.contains(range.commonAncestorContainer)) {
		return null;
	}

	// measuring from the container's start to each boundary copes with boundaries that sit on
	// element nodes (e.g. triple-click selections) as well as on text nodes
	const measure = (boundaryNode, boundaryOffset) => {
		const prefix = document.createRange();
		prefix.setStart(container, 0);
		prefix.setEnd(boundaryNode, boundaryOffset);
		return prefix.toString().length;
	};

	const start = measure(range.startContainer, range.startOffset);
	const end = measure(range.endContainer, range.endOffset);
	return start < end ? {start, end} : null;
};

const createMark = (start, end, onClick) => {
	const mark = document.createElement('mark');
	mark.setAttribute(MARK_ATTR, `${start}-${end}`);
	mark.style.backgroundColor = HIGHLIGHT_COLOUR;
	mark.style.color = 'inherit';
	mark.style.borderRadius = '2px';
	mark.style.cursor = 'pointer';
	mark.title = 'Click to remove highlight';

	if (onClick) {
		mark.addEventListener('click', (e) => {
			e.stopPropagation();
			onClick(start, end);
		});
	}

	return mark;
};

// unwraps every <mark> added by this feature, merging the split text back together
const removeExistingHighlights = (container) => {
	container.querySelectorAll(`mark[${MARK_ATTR}]`).forEach((mark) => {
		const parent = mark.parentNode;
		if (!parent) {
			return;
		}
		while (mark.firstChild) {
			parent.insertBefore(mark.firstChild, mark);
		}
		parent.removeChild(mark);
		parent.normalize();
	});
};

// wraps the [start, end) range, one <mark> per text node it touches
const applyHighlight = (container, start, end, onClick) => {
	const segments = [];
	let offset = 0;

	for (const node of getTextNodes(container)) {
		const nodeStart = offset;
		const nodeEnd = offset + node.length;
		offset = nodeEnd;

		if (nodeEnd <= start) {
			continue;
		}
		if (nodeStart >= end) {
			break;
		}
		segments.push({node, from: Math.max(start, nodeStart) - nodeStart, to: Math.min(end, nodeEnd) - nodeStart});
	}

	segments.forEach(({node, from, to}) => {
		// whitespace-only runs are the gaps between block elements (and inside tables would
		// even be invalid places for a <mark>) - skipping them doesn't affect any offsets
		if (!node.data.slice(from, to).trim()) {
			return;
		}

		let target = node;
		if (to < target.length) {
			target.splitText(to);
		}
		if (from > 0) {
			target = target.splitText(from);
		}

		const mark = createMark(start, end, onClick);
		target.parentNode.insertBefore(mark, target);
		mark.appendChild(target);
	});
};

// clears whatever is painted, then paints each saved highlight
const applyHighlights = (container, highlights, onClick) => {
	removeExistingHighlights(container);
	highlights.forEach(({start, end}) => applyHighlight(container, start, end, onClick));
};

export {getSelectionOffsets, applyHighlights}
